export interface FaqItem {
  /**
   * Stabile, sprachunabhängige Kennung der Frage. Sie ist der Anker für
   * `/faq#<id>` — die FAQ-Seite klappt die Frage auf und scrollt sie in den
   * Sichtbereich. Deshalb: einmal vergeben, nie wieder ändern (bestehende
   * Links, z. B. aus dem Kontaktformular oder aus E-Mails, laufen sonst ins
   * Leere).
   */
  id: string;
  question: string;
  answer: string;
}

export interface FaqCategory {
  id: string;
  title: string;
  icon: string;
  items: FaqItem[];
}

export function getFaqCategories(): FaqCategory[] {
  return [
    {
      id: 'on-site',
      title: $localize`:@@faq.category.onSite:Betreuung vor Ort`,
      icon: 'place',
      items: [
        {
          id: 'kind-krank',
          question: $localize`:@@faq.onSite.q1:Mein Kind ist krank oder kommt heute nicht — wo melde ich das?`,
          answer: $localize`:@@faq.onSite.a1:Bitte geben Sie immer in der Schule und in der Betreuung vor Ort Bescheid, telefonisch oder persönlich. Dieses Portal ist nur für Fragen rund um den Betreuungsvertrag da — eine Nachricht hier erreicht die Betreuung vor Ort nicht rechtzeitig.`,
        },
        {
          id: 'kind-frueher-abholen',
          question: $localize`:@@faq.onSite.q2:Mein Kind soll heute früher abgeholt werden oder allein nach Hause gehen`,
          answer: $localize`:@@faq.onSite.a2:Bitte geben Sie in der Einrichtung vor Ort Bescheid. Dieses System wird in der Verwaltung genutzt und nicht vor Ort. Für den Heimweg müssen Sie keinen Antrag einreichen: Alle Kinder gehen nach Betreuungsende selbstständig nach Hause. Nur wenn Sie damit nicht einverstanden sind, reichen Sie bitte einen Widerspruch in der Einrichtung ein.`,
        },
        {
          id: 'mittagessen-buchen',
          question: $localize`:@@faq.onSite.q3:Bei „Mittagessen" steht „Nein" — wie buche ich das Essen?`,
          answer: $localize`:@@faq.onSite.a3:Das Mittagessen buchen Sie an den meisten Schulen direkt beim Caterer und nicht in diesem Portal. Deshalb kann im Vertrag „Mittagessen: nein" stehen, obwohl Ihr Kind mitisst. An Schulen, bei denen das Essen im Modulpreis enthalten ist, ist es ohnehin gebucht. Bitte sprechen Sie die Einrichtung vor Ort an — dort erfahren Sie, was für Ihre Schule gilt.`,
        },
      ],
    },
    {
      id: 'registration',
      title: $localize`:@@faq.category.registration:Registrierung`,
      icon: 'school',
      items: [
        {
          id: 'anmeldung-starten',
          question: $localize`:@@faq.registration.q1:Wie melde ich mein Kind an?`,
          answer: $localize`:@@faq.registration.a1:Erstellen Sie ein Konto, klicken Sie auf „Anmeldung starten", wählen Sie die Schule und füllen Sie den Anmeldebogen in 7 Schritten aus. Sie erhalten eine Bestätigungs-E-Mail.`,
        },
        {
          id: 'anmeldung-stornieren',
          question: $localize`:@@faq.registration.q2:Kann ich eine Anmeldung stornieren?`,
          answer: $localize`:@@faq.registration.a2:Ja, solange noch kein Vertrag abgeschlossen wurde. Gehen Sie zu „Meine Kinder", wählen Sie das Kind aus, wechseln Sie zum Tab „Vertragsänderungen" und klicken Sie auf „Anmeldung stornieren". Die Stornierung wird sofort wirksam.`,
        },
        {
          id: 'anmeldung-bearbeitungsdauer',
          question: $localize`:@@faq.registration.q3:Wie lange dauert die Bearbeitung meiner Anmeldung?`,
          answer: $localize`:@@faq.registration.a3:Die Bearbeitungszeit hängt von der jeweiligen Schule ab. Sie erhalten eine E-Mail, sobald Ihre Anmeldung bearbeitet wurde.`,
        },
        {
          id: 'nur-essen-buchen',
          question: $localize`:@@faq.registration.q4:Kann ich nur das Essen buchen, ohne Betreuung?`,
          answer: $localize`:@@faq.registration.a4:Nein, eine reine Essensbestellung ist leider nicht möglich. Die Kinder müssen im jeweiligen Programm (z.B. Pakt für den Ganztag, Profil 1 oder Profil 2) der Schule angemeldet werden, um am Essen teilnehmen zu können.`,
        },
        {
          id: 'stammdaten-korrigieren',
          question: $localize`:@@faq.registration.q6:Name oder Geburtsdatum meines Kindes ist falsch — wie wird das korrigiert?`,
          answer: $localize`:@@faq.registration.a6:Diese Angaben stehen im Vertrag und lassen sich deshalb nicht selbst ändern. Schreiben Sie uns bitte über das Kontaktformular, welche Angabe falsch ist und wie sie richtig lautet, und nennen Sie dabei Name und Geburtsdatum des Kindes. Wir korrigieren das und melden uns bei Ihnen. Ihre eigenen Kontaktdaten pflegen Sie in Ihrem Profil selbst.`,
        },
        {
          id: 'notfallkontakt-aendern',
          question: $localize`:@@faq.registration.q7:Wie ändere ich den Notfallkontakt oder die Angabe „mitversichert bei"?`,
          answer: $localize`:@@faq.registration.a7:Diese Angaben lassen sich nach der Anmeldung nicht selbst ändern. Schicken Sie uns die neuen Angaben bitte über das Kontaktformular, zusammen mit Name und Geburtsdatum des Kindes — wir tragen sie nach.`,
        },
      ],
    },
    {
      id: 'contracts',
      title: $localize`:@@faq.category.contracts:Verträge`,
      icon: 'description',
      items: [
        {
          id: 'vertrag-finden',
          question: $localize`:@@faq.contracts.q1:Wo finde ich meinen Vertrag?`,
          answer: $localize`:@@faq.contracts.a1:Gehen Sie zu „Meine Kinder", wählen Sie Ihr Kind und öffnen Sie den Tab „Betreuung". Dort sehen Sie alle Vertragsdetails.`,
        },
        {
          id: 'vertrag-kuendigen',
          question: $localize`:@@faq.contracts.q2:Wie kündige ich meinen Vertrag?`,
          answer: $localize`:@@faq.contracts.a2:Gehen Sie zu „Meine Kinder", wählen Sie das Kind, wechseln Sie zum Tab „Vertragsänderungen" und klicken Sie auf „Vertrag kündigen". Wählen Sie das gewünschte Enddatum (Kündigungsfrist: 6 Wochen vor dem 31.01. oder 31.07. eines Jahres). Bei Umzug oder Schulwechsel besteht ein Sonderkündigungsrecht. Die Kündigung muss geprüft und genehmigt werden.`,
        },
        {
          id: 'modul-wechseln',
          question: $localize`:@@faq.contracts.q3:Kann ich das Betreuungsmodul wechseln?`,
          answer: $localize`:@@faq.contracts.a3:Ja, jeweils zum 01.02. oder zum 01.08. eines Jahres. Gehen Sie zu „Meine Kinder" → Kind auswählen → Tab „Vertragsänderungen" → „Modul wechseln". Wählen Sie das neue Modul und als Startdatum den nächsten 01.02. oder 01.08. Bearbeitungszeit: 1–3 Werktage.`,
        },
        {
          id: 'steuerbescheinigung',
          question: $localize`:@@faq.contracts.q4:Ich brauche eine Bescheinigung über die Betreuungskosten für das Finanzamt`,
          answer: $localize`:@@faq.contracts.a4:Die Verwaltung stellt keine Bescheinigungen für die Steuererklärung mehr aus: Die Pflicht, dem Finanzamt Belege vorzulegen, ist seit dem Veranlagungsjahr 2017 entfallen. Nur wenn das Finanzamt eine Bescheinigung ausdrücklich anfordert, stellen wir sie aus. Bescheinigungen über Betreuungs- oder Essenskosten für einen Antrag bei einem Amt (z. B. Jobcenter) erhalten Sie weiterhin. In beiden Fällen schicken Sie uns die Anfrage über das Kontaktformular, zusammen mit Name und Geburtsdatum des Kindes und dem Jahr — bei einer Anforderung des Finanzamts bitte auch dessen Schreiben. Ihren Vertrag finden Sie unter „Meine Kinder" → Kind → Tab „Betreuung".`,
        },
      ],
    },
    {
      id: 'alterations',
      title: $localize`:@@faq.category.alterations:Änderungsanträge`,
      icon: 'swap_horiz',
      items: [
        {
          id: 'aenderungsantrag-was-ist-das',
          question: $localize`:@@faq.alterations.q1:Was ist ein Änderungsantrag?`,
          answer: $localize`:@@faq.alterations.a1:Ein Änderungsantrag ist eine Anfrage zur Änderung Ihres Vertrags — z. B. Modulwechsel, Mittagessen ändern oder Vertrag kündigen. Alles online, keine Papierformulare.`,
        },
        {
          id: 'aenderungsantrag-zurueckziehen',
          question: $localize`:@@faq.alterations.q2:Kann ich einen Änderungsantrag zurückziehen?`,
          answer: $localize`:@@faq.alterations.a2:Ja, solange er noch nicht genehmigt oder abgelehnt wurde. Gehen Sie zu „Meine Kinder" → Kind → Tab „Vertragsänderungen" und klicken Sie auf „Stornieren".`,
        },
        {
          id: 'aenderungsantrag-bearbeitungsdauer',
          question: $localize`:@@faq.alterations.q3:Wie lange dauert die Bearbeitung?`,
          answer: $localize`:@@faq.alterations.a3:Die Bearbeitungszeit hängt vom jeweiligen Antrag ab. Sie erhalten eine E-Mail, sobald Ihr Antrag bearbeitet wurde.`,
        },
      ],
    },
    {
      id: 'payments',
      title: $localize`:@@faq.category.payments:Zahlungen`,
      icon: 'payment',
      items: [
        {
          id: 'bezahlung',
          question: $localize`:@@faq.payments.q1:Wie bezahle ich die Betreuung?`,
          answer: $localize`:@@faq.payments.a1:Per SEPA-Lastschrift (automatischer Bankeinzug), jeweils zu Beginn des Monats.`,
        },
        {
          id: 'abbuchungstermin',
          question: $localize`:@@faq.payments.q4:Wann wird abgebucht?`,
          answer: $localize`:@@faq.payments.a4:Wir buchen die Betreuungsgebühr immer zu Beginn des Monats ab. Ein anderer Termin lässt sich nicht wählen.`,
        },
        {
          id: 'abbuchung-trotz-kuendigung',
          question: $localize`:@@faq.payments.q5:Warum wurde trotz Kündigung oder Abmeldung noch abgebucht?`,
          answer: $localize`:@@faq.payments.a5:Bis zum Ende der Kündigungsfrist läuft der Vertrag weiter — die Gebühr wird also auch für den letzten Vertragsmonat eingezogen. Wurde nach dem Vertragsende abgebucht, schreiben Sie uns bitte über das Kontaktformular: mit Name und Geburtsdatum des Kindes und dem Datum der Abbuchung. Wir prüfen den Fall und melden uns bei Ihnen.`,
        },
        {
          id: 'bankverbindung-aendern',
          question: $localize`:@@faq.payments.q2:Kann ich die Bankverbindung ändern?`,
          answer: $localize`:@@faq.payments.a2:Ja. Gehen Sie zu „Meine Kinder" → Kind → Tab „Vertragsänderungen" → „Bankverbindung ändern".`,
        },
        {
          id: 'auslaendisches-konto',
          question: $localize`:@@faq.payments.q3:Kann ich ein ausländisches Bankkonto verwenden?`,
          answer: $localize`:@@faq.payments.a3:Nein, aktuell sind nur deutsche Konten möglich. Bei der Anmeldung und beim Ändern der Bankverbindung akzeptiert das Portal ausschließlich deutsche IBANs (beginnend mit „DE"). Eine ausländische IBAN (z. B. AT…, PT…, NL…) wird abgelehnt.`,
        },
      ],
    },
    {
      id: 'holiday-care',
      title: $localize`:@@faq.category.holidayCare:Ferienbetreuung`,
      icon: 'beach_access',
      items: [
        {
          id: 'ferienbetreuung-buchen',
          question: $localize`:@@faq.holidayCare.q1:Wie buche ich Ferienbetreuung?`,
          answer: $localize`:@@faq.holidayCare.a1:Gehen Sie zu „Ferienbetreuung" im Menü, wählen Sie Ihr Kind, die gewünschten Ferien und Wochen aus. Die Buchung wird per E-Mail bestätigt.`,
        },
        {
          id: 'ferienbetreuung-stornieren',
          question: $localize`:@@faq.holidayCare.q2:Kann ich eine Ferienbetreuungs-Buchung stornieren?`,
          answer: $localize`:@@faq.holidayCare.a2:Nein. Die Ferienbetreuung wird verbindlich gebucht und kann nicht storniert werden. Ein Sonderkündigungsrecht besteht nur, wenn das Angebot an Ihrem Standort abgesagt wird und Ihr Kind an einem anderen Standort betreut werden soll. Wenden Sie sich in diesem Fall bitte an die Betreuung an Ihrer Schule.`,
        },
        {
          id: 'ferienbetreuung-einzelne-tage',
          question: $localize`:@@faq.holidayCare.q3:Kann ich einzelne Tage buchen?`,
          answer: $localize`:@@faq.holidayCare.a3:Nein, Ferienbetreuung wird wochenweise gebucht. Sie können aber nur die gewünschten Wochen auswählen.`,
        },
      ],
    },
    {
      id: 'technical',
      title: $localize`:@@faq.category.technical:Technische Probleme`,
      icon: 'build',
      items: [
        {
          id: 'seite-laedt-nicht',
          question: $localize`:@@faq.technical.q1:Die Seite lädt nicht. Was tun?`,
          answer: $localize`:@@faq.technical.a1:Aktualisieren Sie die Seite (F5), leeren Sie den Browser-Cache, versuchen Sie einen anderen Browser (Chrome empfohlen) oder überprüfen Sie Ihre Internetverbindung.`,
        },
        {
          id: 'login-problem',
          question: $localize`:@@faq.technical.q2:Ich kann mich nicht anmelden. Was tun?`,
          answer: $localize`:@@faq.technical.a2:Überprüfen Sie E-Mail-Adresse, Passwort und Feststelltaste. Haben Sie den Bestätigungslink geklickt? Nutzen Sie „Passwort vergessen?" oder kontaktieren Sie den Support.`,
        },
        {
          id: 'browser-empfehlung',
          question: $localize`:@@faq.technical.q3:Welchen Browser soll ich verwenden?`,
          answer: $localize`:@@faq.technical.a3:Empfohlen: Google Chrome, Mozilla Firefox, Safari oder Microsoft Edge in der neuesten Version. Internet Explorer wird nicht unterstützt.`,
        },
      ],
    },
  ];
}

/**
 * Sucht eine FAQ-Frage über ihre stabile Kennung. Liefert `undefined`, wenn es
 * die Kennung nicht (mehr) gibt — Aufrufer blenden den Hinweis dann einfach
 * nicht ein, statt auf einen toten Anker zu verlinken.
 */
export function findFaqItemById(id: string): FaqItem | undefined {
  for (const category of getFaqCategories()) {
    const item = category.items.find((entry) => entry.id === id);
    if (item) {
      return item;
    }
  }
  return undefined;
}
